import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, RouterModule } from '@angular/router';
import { Router, IsActiveMatchOptions } from '@angular/router';
import { ADMIN, USER_DATA } from '../../../../core/utils/constant';
import { distinctUntilChanged, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from '../../../services/permission.service';
import { MenuItemStructure, MenuService } from '../../../services/menu.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { IconsService } from '@rieaya-core';
import { InputText } from 'primeng/inputtext';

@Component({
  selector: 'app-menu',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslocoDirective,
    InputText,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.css',
})
export class MenuComponent {
  private menuService = inject(MenuService);
  private router = inject(Router);
  private permissionService = inject(PermissionService);
  private destroyRef = inject(DestroyRef);
  icons = inject(IconsService);

  menuItems: MenuItemStructure[] = [];
  searchControl = new FormControl('', { nonNullable: true });

  private allMenuItems: MenuItemStructure[] = [];

  userData = JSON.parse(localStorage.getItem(USER_DATA) || '');

  ADMIN = ADMIN;

  constructor() {
    effect(() => {
      const menuStructure = this.menuService.getStructure(this.userData.role);

      const filteredMenu = this.filterMenuByPermission(menuStructure);

      this.expandActiveParents(this.router.url, filteredMenu);

      queueMicrotask(() => {
        this.allMenuItems = filteredMenu;
        this.applySearch(this.searchControl.value);
      });
    });

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.applySearch(searchTerm);
      });

    // auto-expand
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        if (this.hasSearchTerm()) {
          return;
        }

        this.expandActiveParents(event.url, this.menuItems);
      });
  }

  isActive(url: string): boolean {
    const options: IsActiveMatchOptions = {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    };
    return this.router.isActive(url, options);
  }

  isExpanded(item: MenuItemStructure): boolean {
    return !!item.children?.length && !!item.expanded;
  }

  toggleItem(item: MenuItemStructure): void {
    item.expanded = !item.expanded;
  }

  hasActiveDescendant(item: MenuItemStructure): boolean {
    return (
      item.children?.some(
        (child) =>
          (!!child.href && this.isActive(child.href)) ||
          this.hasActiveDescendant(child)
      ) ?? false
    );
  }

  trackMenuItem(_: number, item: MenuItemStructure): string {
    return item.href || item.label;
  }

  itemExists(permissionId?: string): boolean {
    if (this.userData.role === ADMIN) {
      return true;
    }

    if (!permissionId) {
      return true;
    }

    return this.permissionService.hasAccess(permissionId);
  }

  private expandActiveParents(
    currentUrl: string,
    items: MenuItemStructure[]
  ): void {
    const cleanUrl = currentUrl.split('?')[0].split('#')[0];

    this.expandParentItemsRecursive(items, cleanUrl);
  }
  private expandParentItemsRecursive(
    items: MenuItemStructure[],
    currentUrl: string
  ): boolean {
    let anItemIsActive = false;

    for (const item of items) {
      let isDirectlyActive = false;
      let hasActiveDescendant = false;

      if (item.children && item.children.length > 0) {
        hasActiveDescendant = this.expandParentItemsRecursive(
          item.children,
          currentUrl
        );
      }

      if (item.href === currentUrl) {
        isDirectlyActive = true;
      }

      if (hasActiveDescendant) {
        item.expanded = true;
      }

      if (isDirectlyActive || hasActiveDescendant) {
        anItemIsActive = true;
      }
    }

    return anItemIsActive;
  }

  private applySearch(searchTerm: string): void {
    const normalizedSearchTerm = this.normalizeSearchText(searchTerm);

    if (!normalizedSearchTerm) {
      this.menuItems = this.allMenuItems;
      this.expandActiveParents(this.router.url, this.menuItems);
      return;
    }

    this.menuItems = this.filterMenuBySearch(
      this.allMenuItems,
      normalizedSearchTerm
    );
  }

  private filterMenuBySearch(
    items: MenuItemStructure[],
    searchTerm: string
  ): MenuItemStructure[] {
    return items.reduce<MenuItemStructure[]>((results, item) => {
      const itemMatches = this.normalizeSearchText(item.label).includes(
        searchTerm
      );
      const childMatches = item.children
        ? this.filterMenuBySearch(item.children, searchTerm)
        : [];

      if (itemMatches) {
        results.push({
          ...item,
          expanded: !!item.children?.length || item.expanded,
          children: item.children
            ? this.cloneMenuItems(item.children, true)
            : undefined,
        });
        return results;
      }

      if (childMatches.length > 0) {
        results.push({
          ...item,
          expanded: true,
          children: childMatches,
        });
      }

      return results;
    }, []);
  }

  private cloneMenuItems(
    items: MenuItemStructure[],
    expandGroups = false
  ): MenuItemStructure[] {
    return items.map((item) => ({
      ...item,
      expanded: expandGroups && item.children?.length ? true : item.expanded,
      children: item.children
        ? this.cloneMenuItems(item.children, expandGroups)
        : undefined,
    }));
  }

  private hasSearchTerm(): boolean {
    return !!this.normalizeSearchText(this.searchControl.value);
  }

  private normalizeSearchText(value = ''): string {
    return value
      .toLocaleLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u064b-\u065f\u0670\u0640]/g, '')
      .replace(/[أإآ]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/ة/g, 'ه');
  }

  private filterMenuByPermission(
    items: MenuItemStructure[]
  ): MenuItemStructure[] {
    const filteredItems: MenuItemStructure[] = [];

    for (const item of items) {
      let filteredChildren: MenuItemStructure[] | undefined = undefined;
      let hasVisibleChildren = false;

      if (item.children && item.children.length > 0) {
        filteredChildren = this.filterMenuByPermission(item.children);
        hasVisibleChildren = filteredChildren.length > 0;
      }

      const isLink = !!item.href;
      const isParent = !!item.children;
      const canAccessLink = isLink && this.itemExists(item.permissionId);

      if (canAccessLink || (isParent && hasVisibleChildren)) {
        filteredItems.push({
          ...item,
          children: filteredChildren,
        });
      }
    }

    return filteredItems;
  }
}
